<script setup lang="ts">
// On app mount + tab visibility change, validate stored license against server
// (throttled to 6h via dumpdeck-license-last-check localStorage key).
//
// Catches revocations from refund/cancel — server deletes the KV entry,
// frontend detects 404 on next /api/license call → clear localStorage →
// downgrade to Free.
//
// Network errors don't trigger downgrade (assume license still valid).
// License is also re-validated client-side (Ed25519 sig + expiry) on every
// isPro computed access.
import { onMounted, onUnmounted } from 'vue';
import { useLicense } from '@/composables/useLicense';
import AppFooter from '@/components/common/AppFooter.vue';

const { validateStoredLicense, currentLicense } = useLicense();

async function checkLicense() {
  if (!currentLicense.value) return; // no license to validate
  const result = await validateStoredLicense();
  if (result === 'revoked') {
    console.warn('License revoked on server. Cleared local license. Pro downgraded to Free.');
    // Optional: show a toast/modal asking user to renew
  } else if (result === 'updated') {
    console.log('License updated on server (plan change).');
  }
}

// Re-validate when user returns to the tab (after switching to other tabs /
// minimizing browser). The validateStoredLicense() function internally
// respects the 6h throttle, so this won't spam KV on every tab focus.
function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    checkLicense();
  }
}

onMounted(() => {
  checkLicense();
  document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<template>
  <router-view />
  <AppFooter />
</template>

<style>
#app { min-height: 100vh; display: flex; flex-direction: column; }
</style>
